import type { PageExperience } from '@/features/experiences/types';
import {
  type BlockLayouts,
  BlockTypes,
  type ExperienceLayouts,
} from '@/types/gql.generated';
import { SidebarRowSelect } from '../SidebarRowSelect';
import { useCustomizeControl } from '../useCustomizeControl';
import { getGroupByOptions } from './groupBy';

type Props = {
  experience: PageExperience;
  onSetBlockLayout: (blockId: string, layout: BlockLayouts) => void;
  onTrackGroupBy: (
    collectionLayout: BlockLayouts,
    pageLayout: ExperienceLayouts
  ) => void;
};

export const GroupBySelect = ({
  experience,
  onSetBlockLayout,
  onTrackGroupBy,
}: Props) => {
  const { register } = useCustomizeControl();

  const collectionBlock = experience.blocks.find(
    (block) => block.type === BlockTypes.Collection
  );
  const collectionBlockOptions = experience.layout.blocks.find(
    (item) => item.type === BlockTypes.Collection
  );
  const options = getGroupByOptions(collectionBlockOptions?.layouts ?? []);

  return (
    <SidebarRowSelect
      options={options}
      // Grouping lives on the Collection block, not on a field of the
      // experience, so both directions are mapped: the preview re-lays that one
      // block within the whole array, and the commit sends only the delta.
      {...register<'blocks', string>('child', 'blocks', {
        toControl: (blocks) =>
          blocks.find((block) => block.type === BlockTypes.Collection)
            ?.layoutId ??
          options[0]?.id ??
          '',
        toField: (id) =>
          experience.blocks.map((block) =>
            block.id === collectionBlock?.id
              ? { ...block, layoutId: id as BlockLayouts }
              : block
          ),
        commit: (id) => {
          if (collectionBlock && experience.layoutId) {
            onTrackGroupBy(id as BlockLayouts, experience.layoutId);
            onSetBlockLayout(collectionBlock.id, id as BlockLayouts);
          }
        },
      })}
    />
  );
};
